import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ScheduledPostStatus } from '../types';
import { useDisplayPostStatus } from '../displayPostStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostStatusPillProps {
  status: ScheduledPostStatus;
  /** With it, a scheduled post whose time has passed shows "Publishing…" (`displayPostStatus`). */
  scheduledAt?: string;
}

/** Where a post is in its life. The copy says what happens next, not a code word. */
const PostStatusPillComponent: React.FC<PostStatusPillProps> = ({ status: storedStatus, scheduledAt }) => {
  const intl = useIntl();
  // With no time, the hook gets a time that never passes.
  const status = useDisplayPostStatus({ status: storedStatus, scheduledAt: scheduledAt ?? '' });

  switch (status) {
    case 'posted':
      return (
        <span className={clsx(styles.pill, styles.pillLive)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.posted', defaultMessage: 'Posted' })}
        </span>
      );
    case 'awaitingManualPost':
      return (
        <span className={clsx(styles.pill, styles.pillPending)}>
          {intl.formatMessage({
            id: 'app.sml.promote.post.status.awaiting',
            defaultMessage: 'Waiting for you to post',
          })}
        </span>
      );
    case 'publishing':
      return (
        <span className={clsx(styles.pill, styles.pillPending)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.publishing', defaultMessage: 'Publishing…' })}
        </span>
      );
    case 'failed':
      return (
        <span className={clsx(styles.pill, styles.pillFailed)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.failed', defaultMessage: 'Failed' })}
        </span>
      );
    case 'canceled':
      return (
        <span className={clsx(styles.pill, styles.pillOff)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.canceled', defaultMessage: 'Canceled' })}
        </span>
      );
    case 'rendering':
      return (
        <span className={clsx(styles.pill, styles.pillNeutral)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.rendering', defaultMessage: 'Rendering the clip' })}
        </span>
      );
    case 'ready':
      return (
        <span className={clsx(styles.pill, styles.pillNeutral)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.ready', defaultMessage: 'Ready' })}
        </span>
      );
    case 'draft':
      return (
        <span className={clsx(styles.pill, styles.pillOff)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.draft', defaultMessage: 'Draft' })}
        </span>
      );
    case 'scheduled':
    default:
      return (
        <span className={clsx(styles.pill, styles.pillNeutral)}>
          {intl.formatMessage({ id: 'app.sml.promote.post.status.scheduled', defaultMessage: 'Scheduled' })}
        </span>
      );
  }
};

export const PostStatusPill = TypedMemo(PostStatusPillComponent);
